import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, ChevronDown, Bookmark, Bell, LogOut } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useUnreadCount } from "../api/hooks";

const AccountMenu = () => {
    const { user, logout } = useAuth();
    const { data: unread } = useUnreadCount();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const doLogout = async () => {
        setOpen(false);
        await logout();
        navigate("/");
    };

    const count = unread?.count || 0;

    return (
        <div className="relative" ref={ref} data-testid="nav-user-menu">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid="nav-account-button"
                className="inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-saffron"
            >
                <User className="h-4 w-4 text-saffron" />
                {user?.name?.split(" ")[0] || "Account"}
                {count > 0 && <span className="h-2 w-2 rounded-full bg-saffron" aria-label={`${count} unread notifications`} />}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    data-testid="account-dropdown"
                    className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-2xl border border-ink/10 bg-white p-2 shadow-xl"
                >
                    <Link to="/profile" onClick={() => setOpen(false)} data-testid="menu-profile-link" className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-ink transition-colors hover:bg-cream hover:text-saffron">
                        <User className="h-4 w-4 text-smoke" /> Profile
                    </Link>
                    <Link to="/saved" onClick={() => setOpen(false)} data-testid="menu-saved-link" className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-ink transition-colors hover:bg-cream hover:text-saffron">
                        <Bookmark className="h-4 w-4 text-smoke" /> Saved offers
                    </Link>
                    <Link to="/notifications" onClick={() => setOpen(false)} data-testid="menu-notifications-link" className="flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-medium text-ink transition-colors hover:bg-cream hover:text-saffron">
                        <Bell className="h-4 w-4 text-smoke" /> Notifications
                        {count > 0 && (
                            <span className="ml-auto rounded-full bg-saffron px-2 py-0.5 text-[10px] font-bold text-white" data-testid="unread-badge">{count}</span>
                        )}
                    </Link>
                    <button
                        type="button"
                        onClick={doLogout}
                        data-testid="nav-logout-button"
                        className="mt-1 flex w-full items-center gap-2.5 rounded-xl border-t border-ink/5 px-4 py-3 pt-3.5 text-left text-sm font-medium text-smoke transition-colors hover:bg-cream hover:text-saffron"
                    >
                        <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
            )}
        </div>
    );
};

export default AccountMenu;
